import {
  AcUnit,
  Air,
  Close,
  Cloud,
  CloudQueue,
  Delete,
  DeviceThermostat,
  DirectionsRun,
  Edit,
  Foggy,
  Grain,
  NightsStay,
  Thunderstorm,
  Umbrella,
  WaterDrop,
  WbCloudy,
  WbSunny,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { ClothingSet } from '@shared/types';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteClothingSet } from '../../services/clothingSetsService';
import {
  getCachedWeather,
  getDailyWeatherForLocation,
  getHourlyWeatherForLocation,
  getUserLocation,
  getWeatherIcon,
  interpretWeatherCode,
  type DailyWeatherData,
  type HourlyWeatherData,
  type WeatherData,
} from '../../services/weatherService';
import {
  getClothingRecommendation,
  type ClothingItem,
  type ClothingRecommendation,
} from '../../utils/clothingRecommendation';
import { LoadingAnimation } from '../common/LoadingAnimation';

const getClothingImage = (item: ClothingItem): string => {
  switch (item) {
    case 't-shirt-short':
      return '/icons/cloths/tshirt short.png';
    case 't-shirt-long':
      return '/icons/cloths/tshirt long.png';
    case 'sweater':
      return '/icons/cloths/sweater.png';
    case 'shorts':
      return '/icons/cloths/shorts.png';
    case 'long-tights':
      return '/icons/cloths/thights.png';
    case 'running-cap':
      return '/icons/cloths/cap.png';
    case 'sweatband':
      return '/icons/cloths/sweatband.png';
    case 'gloves':
      return '/icons/cloths/gloves.png';
    case 'buff':
      return '/icons/cloths/buff.png';
    default:
      return '/icons/cloths/tshirt short.png';
  }
};

/**
 * Normalizes clothing recommendation to ensure arrays for top/bottom
 * Handles legacy format where top/bottom were single items
 */
const normalizeClothing = (clothing: any): ClothingRecommendation => {
  return {
    ...clothing,
    top: Array.isArray(clothing.top) ? clothing.top : [clothing.top],
    bottom: Array.isArray(clothing.bottom) ? clothing.bottom : [clothing.bottom],
    accessories: Array.isArray(clothing.accessories) ? clothing.accessories : [],
  };
};

interface WorkoutAvatarWidgetProps {
  /** Optional pre-loaded weather data to avoid duplicate API calls */
  weatherData?: WeatherData | null;
  /** Optional pre-loaded clothing recommendation */
  clothingData?: ClothingRecommendation | null;
  /** Optional loading state from parent */
  isLoadingData?: boolean;
}

/**
 * Widget displaying workout avatar with weather-appropriate clothing
 */
const WorkoutAvatarWidget: React.FC<WorkoutAvatarWidgetProps> = ({
  weatherData,
  clothingData,
  isLoadingData,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isXlScreen = useMediaQuery(theme.breakpoints.up('xl'));
  const isMdScreen = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [weather, setWeather] = useState<WeatherData | null>(weatherData || null);
  const [clothing, setClothing] = useState<ClothingRecommendation | null>(clothingData || null);
  const [isLoading, setIsLoading] = useState(isLoadingData ?? true);
  const [error, setError] = useState<string | null>(null);
  const [weatherModalOpen, setWeatherModalOpen] = useState(false);
  const [hourlyWeather, setHourlyWeather] = useState<HourlyWeatherData[]>([]);
  const [loadingHourly, setLoadingHourly] = useState(false);
  const [dailyWeather, setDailyWeather] = useState<DailyWeatherData[]>([]);
  const [loadingDaily, setLoadingDaily] = useState(false);
  // Multiple matches state
  const [showMatchesDialog, setShowMatchesDialog] = useState(false);
  const [deletingSetId, setDeletingSetId] = useState<string | null>(null);

  // Only load weather if not provided via props
  // Check if isLoadingData prop is explicitly passed (even if undefined) to determine if parent manages data
  const isParentManaged = isLoadingData !== undefined;

  useEffect(() => {
    if (isParentManaged) {
      // Parent is managing data, just sync with props
      setWeather(weatherData || null);
      setClothing(clothingData || null);
      setIsLoading(isLoadingData ?? false);
    } else {
      // Standalone mode - load own data
      loadWeather();
    }
  }, [weatherData, clothingData, isLoadingData, isParentManaged]);

  const loadWeather = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const weatherData = await getCachedWeather();
      setWeather(weatherData);

      // Load clothing recommendation (may include custom sets)
      if (weatherData) {
        const clothingRec = await getClothingRecommendation(weatherData);
        // Normalize to ensure arrays for top/bottom (handles legacy data)
        setClothing(normalizeClothing(clothingRec));
      }
    } catch (err) {
      console.error('Error loading weather:', err);
      setError('Could not load weather data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadHourlyWeather = async () => {
    if (!weather || loadingHourly || hourlyWeather.length > 0) return;

    try {
      setLoadingHourly(true);
      // Get location from cached weather or use default
      const location = await getUserLocation();
      if (location) {
        const hourly = await getHourlyWeatherForLocation(location.latitude, location.longitude);
        setHourlyWeather(hourly);
      }
    } catch (err) {
      console.error('Error loading hourly weather:', err);
    } finally {
      setLoadingHourly(false);
    }
  };

  const loadDailyWeather = async () => {
    if (!weather || loadingDaily || dailyWeather.length > 0) return;

    try {
      setLoadingDaily(true);
      const location = await getUserLocation();
      if (location) {
        const daily = await getDailyWeatherForLocation(location.latitude, location.longitude);
        setDailyWeather(daily);
      }
    } catch (err) {
      console.error('Error loading daily weather:', err);
    } finally {
      setLoadingDaily(false);
    }
  };

  /**
   * Handle deleting a duplicate clothing set
   */
  const handleDeleteSet = async (setId: string) => {
    try {
      setDeletingSetId(setId);
      await deleteClothingSet(setId);

      // Reload weather/clothing to get updated matches
      if (isParentManaged) {
        // If parent manages data, we can't reload here - just close dialog
        // The parent will need to reload on next interaction
        setShowMatchesDialog(false);
      } else {
        await loadWeather();
      }
    } catch (err) {
      console.error('Error deleting clothing set:', err);
      alert('Failed to delete clothing set');
    } finally {
      setDeletingSetId(null);
    }
  };

  // Load hourly and daily weather when modal opens
  useEffect(() => {
    if (weatherModalOpen) {
      if (!loadingHourly && hourlyWeather.length === 0) {
        loadHourlyWeather();
      }
      if (!loadingDaily && dailyWeather.length === 0) {
        loadDailyWeather();
      }
    }
  }, [weatherModalOpen]);

  if (isLoading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <LoadingAnimation
            message="Loading workout recommendations..."
            size="small"
            ariaLabel="Loading workout recommendations"
          />
        </CardContent>
      </Card>
    );
  }

  if (error || !weather || !clothing) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography color="text.secondary" align="center">
            Weather data unavailable
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header - Aligned with other widgets */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsRun sx={{ fontSize: 20 }} />
            <Typography variant="h6" fontSize="1.1rem">
              What to Wear
            </Typography>
          </Box>
        </Box>
        {/* Weather Info and Clothing Row */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: isXlScreen || !isMdScreen ? 'column' : 'row',
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          {/* Weather Info Box - 25% on wide screens, full width on sidebar and mobile */}
          <Box
            onClick={() => setWeatherModalOpen(true)}
            sx={{
              width: isXlScreen || !isMdScreen ? '100%' : '25%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'primary.main',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {(() => {
                const iconName = getWeatherIcon(weather.weatherCode, weather.isDay);
                const iconProps = { sx: { fontSize: 32 } };
                switch (iconName) {
                  case 'WbSunny':
                    return <WbSunny color="warning" {...iconProps} />;
                  case 'NightsStay':
                    return <NightsStay color="primary" {...iconProps} />;
                  case 'WbCloudy':
                    return <WbCloudy color="action" {...iconProps} />;
                  case 'CloudQueue':
                    return <CloudQueue color="action" {...iconProps} />;
                  case 'Foggy':
                    return <Foggy color="action" {...iconProps} />;
                  case 'Grain':
                    return <Grain color="primary" {...iconProps} />;
                  case 'WaterDrop':
                    return <WaterDrop color="primary" {...iconProps} />;
                  case 'AcUnit':
                    return <AcUnit color="info" {...iconProps} />;
                  case 'Thunderstorm':
                    return <Thunderstorm color="warning" {...iconProps} />;
                  default:
                    return <Cloud {...iconProps} />;
                }
              })()}
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {weather.apparentTemperature}°C
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  Actual: {weather.temperature}°C
                </Typography>
              </Box>
            </Box>

            {weather.windSpeed > 15 && (
              <Typography variant="caption" color="text.secondary">
                Wind: {weather.windSpeed} km/h
              </Typography>
            )}
          </Box>

          {/* Clothing Items Box - 75% on wide screens, full width on sidebar and mobile */}
          <Box
            onClick={() => {
              if (clothing.isCustomSet && clothing.customSetId) {
                navigate(`/clothing-sets?edit=${clothing.customSetId}`);
              } else {
                navigate('/clothing-sets');
              }
            }}
            sx={{
              width: isXlScreen || !isMdScreen ? '100%' : '75%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: 2,
              borderColor: 'divider',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: 2,
                transform: 'translateY(-2px)',
              },
            }}
          >
            {/* Clothing Set Name with Multiple Matches Indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textAlign: 'center',
                  fontWeight: 'medium',
                }}
              >
                {clothing.isCustomSet && clothing.customSetName
                  ? `📦 ${clothing.customSetName}`
                  : 'Default clothing set'}
              </Typography>
              {/* Show chip for multiple matches */}
              {clothing.allMatches && clothing.allMatches.length > 1 && (
                <Chip
                  label={`${clothing.allMatches.length} matches`}
                  size="small"
                  color="warning"
                  onClick={e => {
                    e.stopPropagation();
                    setShowMatchesDialog(true);
                  }}
                  sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 20 }}
                  aria-label={`${clothing.allMatches.length} clothing sets match current weather`}
                />
              )}
            </Box>
            {/* Icons - Switch to vertical layout when too many items or in sidebar/mobile */}
            {(() => {
              const totalItems =
                clothing.top.length + clothing.bottom.length + clothing.accessories.length;
              // Use vertical layout on mobile, or in sidebar (when not md+), or when many items
              const useVerticalLayout = (isMobile || !isMdScreen || isXlScreen) && totalItems > 3;

              if (useVerticalLayout) {
                // Vertical stacked layout for many items
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    {/* Accessories Row - only if present */}
                    {clothing.accessories.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        {clothing.accessories.map((item, idx) => (
                          <img
                            key={idx}
                            src={getClothingImage(item)}
                            alt="Accessory"
                            style={{
                              width: 32,
                              height: 32,
                              filter: theme.palette.mode === 'dark' ? 'invert(0.85)' : 'none',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    {/* Divider */}
                    <Box
                      sx={{
                        width: 2,
                        height: 40,
                        bgcolor: 'divider',
                        borderRadius: 1,
                      }}
                    />
                    {/* Top Row */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {clothing.top.map((item, idx) => (
                        <img
                          key={idx}
                          src={getClothingImage(item)}
                          alt="Top"
                          style={{
                            width: 32,
                            height: 32,
                            filter: theme.palette.mode === 'dark' ? 'invert(0.85)' : 'none',
                          }}
                        />
                      ))}
                    </Box>
                    {/* Divider */}
                    <Box
                      sx={{
                        width: 2,
                        height: 40,
                        bgcolor: 'divider',
                        borderRadius: 1,
                      }}
                    />
                    {/* Bottom Row */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {clothing.bottom.map((item, idx) => (
                        <img
                          key={idx}
                          src={getClothingImage(item)}
                          alt="Bottom"
                          style={{
                            width: 32,
                            height: 32,
                            filter: theme.palette.mode === 'dark' ? 'invert(0.85)' : 'none',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              }

              // Horizontal layout for few items (original layout)
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Top */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {clothing.top.map((item, idx) => (
                      <img
                        key={idx}
                        src={getClothingImage(item)}
                        alt="Top"
                        style={{
                          width: 40,
                          height: 40,
                          filter: theme.palette.mode === 'dark' ? 'invert(0.85)' : 'none',
                        }}
                      />
                    ))}
                  </Box>

                  {/* Divider */}
                  <Box
                    sx={{
                      width: 2,
                      height: 40,
                      bgcolor: 'divider',
                      borderRadius: 1,
                    }}
                  />

                  {/* Bottom */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {clothing.bottom.map((item, idx) => (
                      <img
                        key={idx}
                        src={getClothingImage(item)}
                        alt="Bottom"
                        style={{
                          width: 40,
                          height: 40,
                          filter: theme.palette.mode === 'dark' ? 'invert(0.85)' : 'none',
                        }}
                      />
                    ))}
                  </Box>

                  {/* Accessories - with divider if present */}
                  {clothing.accessories.length > 0 && (
                    <>
                      <Box
                        sx={{
                          width: 2,
                          height: 40,
                          bgcolor: 'divider',
                          borderRadius: 1,
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {clothing.accessories.map((item, idx) => (
                          <img
                            key={idx}
                            src={getClothingImage(item)}
                            alt="Accessory"
                            style={{
                              width: 40,
                              height: 40,
                              filter: theme.palette.mode === 'dark' ? 'invert(0.85)' : 'none',
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })()}
          </Box>
        </Box>
      </CardContent>

      {/* Weather Detail Modal */}
      <Dialog
        open={weatherModalOpen}
        onClose={() => setWeatherModalOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Typography variant="h6" component="span" fontWeight="bold">
            Weather Details
          </Typography>
          <IconButton onClick={() => setWeatherModalOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {weather && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
              {/* Hero Section - Temperature & Condition */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: { xs: 2, sm: 3 },
                  bgcolor: weather.isDay ? 'warning.light' : 'primary.dark',
                  borderRadius: 3,
                  color: weather.isDay ? 'warning.contrastText' : 'primary.contrastText',
                }}
              >
                <Box>
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    sx={{ lineHeight: 1, fontSize: { xs: '2.5rem', sm: '3rem' } }}
                  >
                    {weather.temperature}°C
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ mt: 1, opacity: 0.9, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}
                  >
                    {interpretWeatherCode(weather.weatherCode).description}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    Feels like {weather.apparentTemperature}°C
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                  {(() => {
                    const iconName = getWeatherIcon(weather.weatherCode, weather.isDay);
                    const iconProps = { sx: { fontSize: { xs: 60, sm: 80 }, opacity: 0.9 } };
                    switch (iconName) {
                      case 'WbSunny':
                        return <WbSunny {...iconProps} />;
                      case 'NightsStay':
                        return <NightsStay {...iconProps} />;
                      case 'WbCloudy':
                        return <WbCloudy {...iconProps} />;
                      case 'CloudQueue':
                        return <CloudQueue {...iconProps} />;
                      case 'Cloud':
                        return <Cloud {...iconProps} />;
                      case 'Foggy':
                        return <Foggy {...iconProps} />;
                      case 'Grain':
                        return <Grain {...iconProps} />;
                      case 'WaterDrop':
                        return <WaterDrop {...iconProps} />;
                      case 'AcUnit':
                        return <AcUnit {...iconProps} />;
                      case 'Umbrella':
                        return <Umbrella {...iconProps} />;
                      case 'Thunderstorm':
                        return <Thunderstorm {...iconProps} />;
                      default:
                        return <Cloud {...iconProps} />;
                    }
                  })()}
                  <Typography variant="caption" sx={{ mt: 1, opacity: 0.8 }}>
                    {weather.isDay ? 'Daytime' : 'Nighttime'}
                  </Typography>
                </Box>
              </Box>

              {/* Weather Metrics Grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: { xs: 1.5, sm: 2 },
                }}
              >
                {/* Temperature Feel */}
                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: 'background.default',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <DeviceThermostat color="error" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      FEELS LIKE
                    </Typography>
                  </Box>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    {weather.apparentTemperature}°C
                  </Typography>
                </Box>

                {/* Wind */}
                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: 'background.default',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Air color="info" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      WIND SPEED
                    </Typography>
                  </Box>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    {weather.windSpeed} km/h
                  </Typography>
                </Box>

                {/* Precipitation */}
                {weather.precipitation !== undefined && (
                  <Box
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: 'background.default',
                      borderRadius: 2,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <WaterDrop color="primary" />
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        PRECIPITATION
                      </Typography>
                    </Box>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                    >
                      {weather.precipitation} mm
                    </Typography>
                  </Box>
                )}

                {/* Rain Chance */}
                {weather.precipitationProbability !== undefined && (
                  <Box
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: 'background.default',
                      borderRadius: 2,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Umbrella color="primary" />
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        RAIN CHANCE
                      </Typography>
                    </Box>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                    >
                      {weather.precipitationProbability}%
                    </Typography>
                  </Box>
                )}

                {/* Weather Code */}
                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: 'background.default',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Cloud color="action" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      WEATHER CODE
                    </Typography>
                  </Box>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    {weather.weatherCode}
                  </Typography>
                </Box>

                {/* Cloud Cover */}
                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: 'background.default',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CloudQueue color="action" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      CLOUD COVER
                    </Typography>
                  </Box>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    {weather.cloudCover}%
                  </Typography>
                </Box>
              </Box>

              {/* Hourly Forecast for Rest of Day */}
              {loadingHourly && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!loadingHourly && hourlyWeather.length > 0 && (
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ mb: 2, display: 'block' }}
                  >
                    Rest of Today
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: { xs: 1.5, sm: 2 },
                      overflowX: 'auto',
                      pb: 1,
                      '&::-webkit-scrollbar': {
                        height: 6,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,.2)',
                        borderRadius: 3,
                      },
                    }}
                  >
                    {hourlyWeather.map((hour, index) => {
                      const time = new Date(hour.time);
                      const iconName = getWeatherIcon(hour.weatherCode, hour.isDay);
                      const IconComponent =
                        {
                          WbSunny,
                          NightsStay,
                          WbCloudy,
                          CloudQueue,
                          Cloud,
                          Foggy,
                          Grain,
                          WaterDrop,
                          AcUnit,
                          Umbrella,
                          Thunderstorm,
                        }[iconName] || Cloud;

                      return (
                        <Box
                          key={index}
                          sx={{
                            minWidth: { xs: 70, sm: 80 },
                            p: { xs: 1, sm: 1.5 },
                            bgcolor: 'background.default',
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold">
                            {time.getHours()}:00
                          </Typography>
                          <IconComponent
                            color={
                              iconName === 'WbSunny' || iconName === 'WbCloudy'
                                ? 'warning'
                                : iconName === 'NightsStay'
                                  ? 'action'
                                  : 'primary'
                            }
                            sx={{ fontSize: 24 }}
                          />
                          <Typography variant="body2" fontWeight="bold">
                            {hour.temperature}°C
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Air sx={{ fontSize: 12, color: 'text.secondary' }} />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: 10 }}
                            >
                              {hour.windSpeed}
                            </Typography>
                          </Box>
                          {hour.precipitation > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <WaterDrop sx={{ fontSize: 12, color: 'primary.main' }} />
                              <Typography
                                variant="caption"
                                color="primary.main"
                                sx={{ fontSize: 10 }}
                              >
                                {hour.precipitation}mm
                              </Typography>
                            </Box>
                          )}
                          {hour.precipitationProbability > 20 && (
                            <Typography
                              variant="caption"
                              color="primary.main"
                              sx={{ fontSize: 10 }}
                            >
                              {hour.precipitationProbability}%
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Daily Forecast for Rest of Week */}
              {loadingDaily && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!loadingDaily && dailyWeather.length > 0 && (
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ mb: 2, display: 'block' }}
                  >
                    Rest of Week
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {dailyWeather.map((day, index) => {
                      const date = new Date(day.date);
                      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                      const iconName = getWeatherIcon(day.weatherCode, true); // Use daytime icon for daily forecast
                      const IconComponent =
                        {
                          WbSunny,
                          NightsStay,
                          WbCloudy,
                          CloudQueue,
                          Cloud,
                          Foggy,
                          Grain,
                          WaterDrop,
                          AcUnit,
                          Umbrella,
                          Thunderstorm,
                        }[iconName] || Cloud;

                      return (
                        <Box
                          key={index}
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            bgcolor: 'background.default',
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            justifyContent: 'space-between',
                            gap: { xs: 1.5, sm: 2 },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              width: { xs: '100%', sm: 'auto' },
                              justifyContent: { xs: 'space-between', sm: 'flex-start' },
                            }}
                          >
                            <Box sx={{ minWidth: 60 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {dayName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {date.getDate()}/{date.getMonth() + 1}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconComponent
                                color={
                                  iconName === 'WbSunny' || iconName === 'WbCloudy'
                                    ? 'warning'
                                    : iconName === 'NightsStay'
                                      ? 'action'
                                      : 'primary'
                                }
                                sx={{ fontSize: 28 }}
                              />
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              gap: { xs: 2, sm: 3 },
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              width: { xs: '100%', sm: 'auto' },
                            }}
                          >
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: 10 }}
                              >
                                MAX
                              </Typography>
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                              >
                                {day.temperatureMax}°C
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: 10 }}
                              >
                                MIN
                              </Typography>
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                              >
                                {day.temperatureMin}°C
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Air sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {day.windSpeedMax}
                                </Typography>
                              </Box>
                              {day.precipitationSum > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <WaterDrop sx={{ fontSize: 14, color: 'primary.main' }} />
                                  <Typography variant="caption" color="primary.main">
                                    {day.precipitationSum}mm
                                  </Typography>
                                </Box>
                              )}
                              {day.precipitationProbabilityMax > 20 && (
                                <Typography variant="caption" color="primary.main">
                                  {day.precipitationProbabilityMax}%
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
            Weather data from Open-Meteo
          </Typography>
        </DialogActions>
      </Dialog>

      {/* Multiple Matches Dialog */}
      <Dialog
        open={showMatchesDialog}
        onClose={() => setShowMatchesDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" component="span" fontWeight="bold">
            Multiple Matching Sets
          </Typography>
          <IconButton
            onClick={() => setShowMatchesDialog(false)}
            size="small"
            aria-label="Close dialog"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {clothing?.allMatches?.length || 0} clothing sets match the current weather. You can
            delete duplicates to keep only the one you want.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {clothing?.allMatches?.map((set: ClothingSet, index: number) => (
              <Box
                key={set._id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: index === 0 ? 'primary.light' : 'background.default',
                  borderRadius: 2,
                  border: 1,
                  borderColor: index === 0 ? 'primary.main' : 'divider',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {set.name}
                    </Typography>
                    {index === 0 && (
                      <Chip label="Active" size="small" color="primary" sx={{ height: 20 }} />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {set.temperature.min}°C to {set.temperature.max}°C
                    {set.conditions.rain && ' • Rain'}
                    {set.conditions.wind && ' • Wind'}
                    {set.conditions.sun && ' • Sun'}
                  </Typography>
                  {/* Clothing preview */}
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                    {set.clothing.top.map((item, i) => (
                      <img
                        key={`top-${i}`}
                        src={getClothingImage(item)}
                        alt="Top"
                        style={{
                          width: 24,
                          height: 24,
                          filter: theme.palette.mode === 'dark' ? 'invert(0.85)' : 'none',
                        }}
                      />
                    ))}
                    {set.clothing.bottom.map((item, i) => (
                      <img
                        key={`bottom-${i}`}
                        src={getClothingImage(item)}
                        alt="Bottom"
                        style={{
                          width: 24,
                          height: 24,
                          filter: theme.palette.mode === 'dark' ? 'invert(0.85)' : 'none',
                        }}
                      />
                    ))}
                    {set.clothing.accessories.map((item, i) => (
                      <img
                        key={`acc-${i}`}
                        src={getClothingImage(item)}
                        alt="Accessory"
                        style={{
                          width: 24,
                          height: 24,
                          filter: theme.palette.mode === 'dark' ? 'invert(0.85)' : 'none',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Edit this set">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowMatchesDialog(false);
                        navigate(`/clothing-sets?edit=${set._id}`);
                      }}
                      aria-label={`Edit ${set.name}`}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete this set">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => set._id && handleDeleteSet(set._id)}
                      disabled={deletingSetId === set._id}
                      aria-label={`Delete ${set.name}`}
                    >
                      {deletingSetId === set._id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Delete fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setShowMatchesDialog(false)} aria-label="Close dialog">
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowMatchesDialog(false);
              navigate('/clothing-sets');
            }}
            aria-label="Manage all clothing sets"
          >
            Manage All Sets
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default WorkoutAvatarWidget;
